import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Toggle } from "@/components/ui/toggle";
import { type Editor } from "@tiptap/react";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  IndentDecrease,
  IndentIncrease,
  Italic,
  LinkIcon,
  List,
  Redo,
  SeparatorHorizontal,
  Undo,
} from "lucide-react";

type Props = {
  editor: Editor | null;
};

function ToolBar({ editor }: Props) {
  if (!editor) {
    return null;
  }

  const getParagraphSpacing = () => {
    const paragraphClass = editor.getAttributes("paragraph").class;
    if (paragraphClass) {
      const spacing = paragraphClass.split("-")[2];
      return spacing;
    }
    return undefined;
  };

  const getFontSize = () => {
    const fontSize = editor.getAttributes("textStyle").fontSize;
    return fontSize || undefined;
  };

  const setParagraphSpacing = (spacing: string) => {
    if (editor) {
      editor
        .chain()
        .focus()
        .updateAttributes("paragraph", {
          class: `spacing-paragraph-${spacing}`,
        })
        .run();
    }
  };

  const setFontSize = (size: string) => {
    if (editor) {
      const sizeNumber = parseInt(size.replace("px", ""));
      editor.chain().focus().setFontSize(size).run();
      editor
        .chain()
        .focus()
        .setLineHeight(sizeNumber * 1.2 + "px")
        .run();
    }
  };

  const handleLinkUpdate = (url: string, openInNewTab: boolean) => {
    if (url) {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({
          href: url,
          target: openInNewTab ? "_blank" : null,
        })
        .run();
    }
  };

  return (
    <div className="flex justify-between gap-3 border border-input rounded-lg p-1 my-2 bg-[#F7FAFC]">
      <div className="gap-1 flex flex-wrap">
        <Toggle
          size="sm"
          pressed={editor.isActive("bold")}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("italic")}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </Toggle>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={editor.isActive("link") ? "bg-accent text-white" : ""}
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="link">Lien URL</Label>
                <Input
                  id="link"
                  defaultValue={editor.getAttributes("link").href || ""}
                  placeholder="https://example.com"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="newTab"
                  defaultChecked={
                    editor.getAttributes("link").target === "_blank"
                  }
                />
                <Label htmlFor="newTab">Ouvrir dans un nouvel onglet</Label>
              </div>
              <div className="flex justify-between">
                <Button
                  type="button"
                  onClick={() => {
                    const url = (
                      document.getElementById("link") as HTMLInputElement
                    ).value;
                    const newTab = (
                      document.getElementById("newTab") as HTMLInputElement
                    )["ariaChecked"];
                    handleLinkUpdate(url, newTab === "true");
                  }}
                >
                  {editor.isActive("link") ? "Mettre à jour" : "Ajouter"}
                </Button>
                {editor.isActive("link") && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => editor.chain().focus().unsetLink().run()}
                  >
                    Supprimer
                  </Button>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <Toggle
          size="sm"
          pressed={editor.isActive("bulletList")}
          onPressedChange={() =>
            editor.chain().focus().toggleBulletList().run()
          }
        >
          <List className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("horizontalRule")}
          onPressedChange={() =>
            editor.chain().focus().setHorizontalRule().run()
          }
        >
          <SeparatorHorizontal className="h-4 w-4" />
        </Toggle>
        <Button
          size="sm"
          type="button"
          variant="ghost"
          //@ts-expect-error : bad typing
          onClick={() => editor.chain().focus().increaseIndent().run()}
        >
          <IndentIncrease className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          //@ts-expect-error : bad typing
          onClick={() => editor.chain().focus().decreaseIndent().run()}
        >
          <IndentDecrease className="h-4 w-4" />
        </Button>
        <Toggle
          size="sm"
          pressed={editor.isActive("textAlign", { align: "left" })}
          onPressedChange={() =>
            editor.chain().focus().setTextAlign("left").run()
          }
        >
          <AlignLeft className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("textAlign", { align: "center" })}
          onPressedChange={() =>
            editor.chain().focus().setTextAlign("center").run()
          }
        >
          <AlignCenter className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("textAlign", { align: "right" })}
          onPressedChange={() =>
            editor.chain().focus().setTextAlign("right").run()
          }
        >
          <AlignRight className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("textAlign", { align: "justify" })}
          onPressedChange={() =>
            editor.chain().focus().setTextAlign("justify").run()
          }
        >
          <AlignJustify className="h-4 w-4" />
        </Toggle>
        <Toggle
          size={"sm"}
          pressed={editor.isActive("heading", { level: 2 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          H2
        </Toggle>
        <Toggle
          size={"sm"}
          pressed={editor.isActive("heading", { level: 3 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          H3
        </Toggle>
        <Select
          onValueChange={(value) => setFontSize(value)}
          value={getFontSize()}
          defaultValue={getFontSize()}
        >
          <SelectTrigger className="w-fit">
            <SelectValue placeholder="Taille" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="12px">12px</SelectItem>
            <SelectItem value="14px">14px</SelectItem>
            <SelectItem value="16px">16px</SelectItem>
            <SelectItem value="18px">18px</SelectItem>
            <SelectItem value="20px">20px</SelectItem>
          </SelectContent>
        </Select>
        <Select
          onValueChange={(value) => setParagraphSpacing(value)}
          value={getParagraphSpacing()}
          defaultValue={getParagraphSpacing()}
        >
          <SelectTrigger className="w-fit">
            <SelectValue placeholder="Espacement" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="2">2px</SelectItem>
            <SelectItem value="4">4px</SelectItem>
            <SelectItem value="6">6px</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Toggle
          size="sm"
          pressed={editor.isActive("undo")}
          onPressedChange={() => editor.chain().focus().undo().run()}
        >
          <Undo className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("redo")}
          onPressedChange={() => editor.chain().focus().redo().run()}
        >
          <Redo className="h-4 w-4" />
        </Toggle>
      </div>
    </div>
  );
}

export default ToolBar;
